import {
  ArrowLeft,
  ArrowRight,
  BrainCircuit,
  BriefcaseBusiness,
  Clipboard,
  FileQuestion,
  FileText,
  Loader2,
  MessageSquareText,
  Search,
  Sparkles,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  generateInterviewProjectTalk,
  generateInterviewQuestions,
  generateInterviewSelfIntro,
  getMyGithubRepositories,
  getResumeReports,
  isFeatureLimitExceeded,
  simulateInterviewAnswer,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import FeatureLimitCard from '../components/FeatureLimitCard.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { setPageMetadata } from '../page-metadata.js'

const CATEGORY_TABS = [
  { id: 'all', label: '全部' },
  { id: 'tech', label: '技术类' },
  { id: 'ops', label: '产品运营' },
  { id: 'media', label: '设计传媒' },
  { id: 'office', label: '教育文职' },
  { id: 'business', label: '财会商科' },
  { id: 'engineering', label: '工程制造' },
  { id: 'health', label: '医药健康' },
  { id: 'public', label: '公考国企' },
]

const CATEGORY_NAMES = {
  tech: '互联网技术类',
  ops: '产品与运营类',
  media: '设计与传媒类',
  office: '教育与文职类',
  business: '财会与商科类',
  engineering: '工程与制造类',
  health: '医药与健康类',
  public: '公考 / 事业单位 / 国企准备类',
}

const CATEGORY_DEFAULTS = {
  tech: {
    majors: ['计算机', '软件工程', '信息管理', '电子信息', '跨专业转岗'],
    skills: ['专业基础', '软件工具', '项目经验', '问题排查', '沟通表达'],
    work: '做一个可演示项目，整理功能截图、职责说明、关键难点和优化记录。',
    resume: '写清技术栈、负责模块、接口/数据设计、问题解决和最终效果。',
  },
  ops: {
    majors: ['市场营销', '电子商务', '新闻传播', '工商管理', '不限专业'],
    skills: ['用户理解', '内容策划', '数据复盘', '活动执行', '沟通协作'],
    work: '准备 1 个运营案例、3 个选题方案、1 份数据复盘或活动方案。',
    resume: '写清目标人群、执行动作、数据结果、复盘结论和个人贡献。',
  },
  media: {
    majors: ['视觉传达', '广告学', '新闻传播', '网络与新媒体', '数字媒体'],
    skills: ['作品集', '审美表达', '软件工具', '创意说明', '项目复盘'],
    work: '整理 3-5 个作品，补充创作背景、设计目标、过程和效果。',
    resume: '写清作品主题、工具、风格依据、交付物和传播/使用效果。',
  },
  office: {
    majors: ['教育学', '汉语言文学', '英语', '行政管理', '人力资源', '公共管理'],
    skills: ['Office', '表达沟通', '文档整理', '组织协调', '服务意识'],
    work: '准备一份课件/活动组织/文档整理案例，说明流程和结果。',
    resume: '写清具体事务、协作对象、产出文档、效率提升和稳定性。',
  },
  business: {
    majors: ['会计学', '财务管理', '审计学', '经济学', '工商管理', '市场营销'],
    skills: ['Excel', '专业基础', '数据核对', '合规意识', '细心程度'],
    work: '准备课程实训、报表整理、市场调研或销售跟进案例。',
    resume: '写清数据规模、表格能力、流程节点、风险意识和业务结果。',
  },
  engineering: {
    majors: ['机械设计制造', '机电一体化', '车辆工程', '自动化', '智能制造'],
    skills: ['制图软件', '专业基础', '工艺理解', '现场意识', '课程设计'],
    work: '整理课程设计、图纸、实训经历，准备一个能讲清楚的工程案例。',
    resume: '写清图纸/设备/工艺、个人负责部分、问题处理和改进效果。',
  },
  health: {
    majors: ['药学', '临床医学', '护理学', '公共健康', '康复治疗', '医学检验'],
    skills: ['医学/药学基础', '沟通能力', '合规意识', '服务意识', '抗压能力'],
    work: '准备专业基础、沟通案例、实训经历、健康科普或服务流程案例。',
    resume: '写清专业课程、实训场景、服务对象、规范意识和沟通结果。',
  },
  public: {
    majors: ['公共管理', '法学', '汉语言文学', '经济学', '金融学', '不限专业'],
    skills: ['材料写作', '逻辑表达', '政策理解', '组织协调', '服务意识'],
    work: '准备材料整理、活动组织、结构化问答和岗位匹配表达。',
    resume: '写清组织经历、材料能力、服务意识、执行结果和稳定性。',
  },
}

const ROLE_GROUPS = {
  tech: ['Java 后端', '前端开发', '测试工程师', '运维 / 云服务', '数据分析', 'AI 应用开发', '小程序开发', '软件实施工程师'],
  ops: ['产品助理', '用户运营', '内容运营', '新媒体运营', '电商运营', '社群运营', '活动运营', '数据运营'],
  media: ['UI 设计', '平面设计', '视频剪辑', '摄影 / 短视频', '文案策划', '品牌传播', '新媒体编辑', '视觉设计助理'],
  office: ['教师 / 教培助理', '行政助理', '人事助理', '文秘 / 办公室文员', '教务运营', '课程顾问', '招生顾问', '客服专员'],
  business: ['会计助理', '财务助理', '审计助理', '出纳', '市场营销', '商务助理', '采购助理', '销售管培生'],
  engineering: ['机械助理工程师', '电气助理工程师', '自动化助理工程师', '质量工程师', '生产计划', '供应链助理', '工艺助理工程师', '设备维护助理'],
  health: ['医药代表', '护理相关岗位', '健康管理助理', '药店 / 药企实习', '医疗运营助理', '康复治疗助理', '医学检验相关岗位', '医疗客服'],
  public: ['行测基础', '申论基础', '结构化面试', '无领导小组讨论', '岗位匹配表达', '材料整理', '国企综合岗', '银行柜员 / 客户经理'],
}

const ROLE_OVERRIDES = {
  '会计助理': {
    majors: ['会计学', '财务管理', '审计学', '工商管理', '经济学'],
    skills: ['Excel', '基础会计', '财务报表', '凭证', '税务基础', '细心程度'],
    questions: ['你为什么选择财务岗位？', '你熟悉哪些 Excel 功能？', '你怎么看待财务工作的细致性？', '如果账目出现差异，你会怎么处理？', '你有没有做过财务相关课程设计或实训？'],
    work: '准备一个财务分析或报表整理案例，最好能展示 Excel 表格、课程实训或凭证整理过程。',
    routeSummary: '先整理课程实训和 Excel 能力，再准备一个财务分析或报表整理案例。',
  },
  '新媒体运营': {
    majors: ['新闻传播', '汉语言文学', '市场营销', '电子商务', '广告学', '网络与新媒体'],
    skills: ['内容选题', '账号运营', '数据复盘', '短视频脚本', '热点分析'],
    questions: ['你做过哪些账号或内容？', '你怎么判断一篇内容效果好不好？', '你如何做选题？', '如果数据不好，你会怎么复盘？', '你常用哪些内容平台？'],
    work: '准备 1 个账号案例、3 个选题方案、1 份内容数据复盘。',
    routeSummary: '准备 1 个账号案例、3 个选题方案、1 份内容数据复盘。',
  },
  '机械助理工程师': {
    majors: ['机械设计制造', '机电一体化', '车辆工程', '智能制造', '材料成型'],
    skills: ['CAD', '机械制图', '材料', '工艺', '设备基础', '课程设计'],
    questions: ['你怎么看图纸？', '你做过哪些课程设计？', '你了解哪些加工工艺？', '项目中遇到过什么结构问题？', '你对生产现场有什么理解？'],
    work: '整理课程设计、图纸、实训经历，准备一个能讲清楚的机械项目案例。',
    routeSummary: '整理课程设计、图纸、实训经历，准备一个能讲清楚的机械项目案例。',
  },
  '教师 / 教培助理': {
    majors: ['师范类', '教育学', '心理学', '汉语言文学', '英语', '学前教育'],
    skills: ['表达能力', '教学设计', '课堂管理', '课件制作', '学生沟通'],
    questions: ['你为什么想做教育？', '你如何设计一节课？', '如果学生不配合你怎么办？', '你有什么试讲经验？', '你怎么看待因材施教？'],
    work: '准备一份试讲稿、一份课件、一个学生辅导案例。',
    routeSummary: '准备一份试讲稿、一份课件、一个学生辅导案例。',
  },
  '行政助理': {
    majors: ['行政管理', '人力资源', '工商管理', '文秘', '公共管理', '汉语言文学'],
    skills: ['Office', '沟通协调', '文件整理', '会议安排', '执行力'],
    questions: ['你如何安排多项任务？', '你熟悉哪些办公软件？', '如果临时任务很多，你怎么处理？', '你如何和不同部门沟通？', '你怎么看待行政工作的琐碎性？'],
    work: '准备办公软件能力、活动组织经历、文档整理案例。',
    routeSummary: '准备办公软件能力、活动组织经历、文档整理案例。',
  },
  '医药代表': {
    majors: ['药学', '临床医学', '护理学', '市场营销', '生物医药相关专业'],
    skills: ['医学/药学基础', '沟通能力', '抗压能力', '产品学习能力'],
    questions: ['你为什么想做医药代表？', '你如何理解销售和专业知识的关系？', '你能接受外勤和压力吗？', '你如何学习一个新产品？', '你如何和客户建立信任？'],
    work: '准备专业基础、沟通案例、抗压经历和岗位理解。',
    routeSummary: '准备专业基础、沟通案例、抗压经历和岗位理解。',
  },
}

function roleSlug(name) {
  return encodeURIComponent(String(name).replace(/\s+/g, '-'))
}

function defaultQuestions(name, category) {
  return [
    `你为什么想做${name}？`,
    `你认为${name}最重要的能力是什么？`,
    '请介绍一段最能证明你能力的经历。',
    '如果工作中遇到紧急任务或结果不理想，你会怎么处理？',
    `你对${CATEGORY_NAMES[category]}方向有什么理解？`,
  ]
}

function buildRole(name, category) {
  const defaults = CATEGORY_DEFAULTS[category]
  const override = ROLE_OVERRIDES[name] || {}
  const skills = override.skills || defaults.skills
  const questions = override.questions || defaultQuestions(name, category)
  const routeSummary = override.routeSummary || `先理解${name}的岗位职责，再整理简历证据、作品案例和常见问题回答。`
  return {
    id: roleSlug(name),
    name,
    category,
    categoryName: CATEGORY_NAMES[category],
    majors: override.majors || defaults.majors,
    fit: override.fit || `适合对${CATEGORY_NAMES[category]}感兴趣，愿意从基础执行、沟通协作和真实案例积累开始的学生。`,
    skills,
    questions,
    work: override.work || defaults.work,
    resume: override.resume || defaults.resume,
    routeSummary,
    route: [
      `第 1 步：了解${name}的日常职责、能力要求和面试流程。`,
      '第 2 步：整理简历，把课程、实训、项目、社团或实习经历改成岗位语言。',
      `第 3 步：准备作品 / 项目，重点展示${skills.slice(0, 3).join('、')}。`,
      '第 4 步：练习常见问题，按“背景 → 行动 → 结果 → 复盘”回答。',
      '第 5 步：模拟面试，记录卡顿问题并二次优化表达。',
    ],
    questionDetails: questions.map((question) => ({
      question,
      expects: '面试官想确认你是否理解岗位、是否有真实准备，以及能否把经历讲清楚。',
      structure: '岗位理解 → 个人经历 → 具体行动 → 结果或复盘。',
      sample: `我理解${name}需要${skills.slice(0, 2).join('和')}。我过去在课程/项目/实践中做过相关准备，例如整理资料、完成案例或参与活动，所以我会从基础执行做起，并持续复盘改进。`,
    })),
  }
}

const ROLES = Object.entries(ROLE_GROUPS).flatMap(([category, names]) => names.map((name) => buildRole(name, category)))

function asList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function quotaText(user, language) {
  const membership = user?.membership || {}
  const item = user?.usageSummary?.interview_generate || {}
  const isVip = Boolean(membership.isVip || membership.isAdmin || membership.tier === 'vip' || membership.tier === 'admin')
  if (item.limit === null) return language === 'en' ? 'Unlimited interview generation' : '面试生成不限额度'
  const limit = Number.isFinite(Number(item.limit)) ? Number(item.limit) : (isVip ? 50 : 5)
  const remaining = item.remaining ?? Math.max(0, limit - Number(item.used || 0))
  return language === 'en'
    ? (isVip ? `Today remaining ${remaining} / ${limit}` : `This month remaining ${remaining} / ${limit}`)
    : (isVip ? `今日剩余 ${remaining} / ${limit}` : `本月剩余 ${remaining} / ${limit}`)
}

function assistantUrl(role) {
  const params = new URLSearchParams({
    role: role.name,
    roleName: role.name,
    category: role.categoryName,
    roleCategory: role.categoryName,
    recommendedMajors: role.majors.join('、'),
    requiredSkills: role.skills.join('、'),
    commonQuestions: role.questions.join('；'),
  })
  return `/career-assistant?${params.toString()}`
}

function RoleCard({ role }) {
  return (
    <article className="interview-role-card">
      <div className="interview-role-card-top">
        <span>{role.categoryName}</span>
        <h3>{role.name}</h3>
      </div>
      <div className="interview-major-tags">
        {role.majors.slice(0, 4).map((major) => <span key={major}>{major}</span>)}
      </div>
      <dl>
        <dt>适合人群</dt><dd>{role.fit}</dd>
        <dt>必备基础</dt><dd>{role.skills.slice(0, 5).join(' / ')}</dd>
        <dt>常见问题</dt><dd>{role.questions.slice(0, 2).join(' / ')}</dd>
        <dt>准备路线</dt><dd>{role.routeSummary}</dd>
        <dt>可做项目 / 作品</dt><dd>{role.work}</dd>
        <dt>简历怎么写</dt><dd>{role.resume}</dd>
      </dl>
      <div className="interview-card-actions">
        <Link className="entry-card-button" to={`/interview/${role.id}`}>查看详情 <ArrowRight size={16} /></Link>
        <Link className="secondary-button" to={assistantUrl(role)}>开始准备</Link>
      </div>
    </article>
  )
}

function CopyBlock({ children, onCopy, title }) {
  if (!children) return null
  return (
    <article className="interview-ai-block">
      <h3>{title}</h3>
      <p>{children}</p>
      <button type="button" onClick={() => onCopy(children)}><Clipboard size={15} />复制</button>
    </article>
  )
}

function ListBlock({ items, title }) {
  const list = asList(items)
  if (!list.length) return null
  return (
    <section className="interview-ai-block">
      <h3>{title}</h3>
      <ul>{list.map((item, index) => <li key={`${title}-${index}`}>{typeof item === 'string' ? item : item.question || item.answer || JSON.stringify(item)}</li>)}</ul>
    </section>
  )
}

function InterviewPrepPage() {
  const { language } = useLanguage()
  const { user, refreshUser } = useAuth() || {}
  const { roleId } = useParams()
  const [activeCategory, setActiveCategory] = useState('all')
  const [keyword, setKeyword] = useState('')
  const [duration, setDuration] = useState('60s')
  const [resumeReports, setResumeReports] = useState([])
  const [githubProjects, setGithubProjects] = useState([])
  const [resumeReportId, setResumeReportId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [jdText, setJdText] = useState('')
  const [intro, setIntro] = useState(null)
  const [projectTalk, setProjectTalk] = useState(null)
  const [questionPack, setQuestionPack] = useState(null)
  const [activeQuestion, setActiveQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [review, setReview] = useState(null)
  const [loading, setLoading] = useState('')
  const [error, setError] = useState('')
  const [limitError, setLimitError] = useState(null)
  const role = ROLES.find((item) => item.id === roleId || decodeURIComponent(item.id) === roleId)

  useEffect(() => {
    setPageMetadata({
      title: '面试准备 - 青途 AI',
      description: '覆盖技术、运营、设计、教育、财会、制造、医药、公考国企等方向的面试准备。',
    })
  }, [])

  useEffect(() => {
    if (user && typeof refreshUser === 'function') refreshUser().catch(() => {})
  }, [refreshUser, user])

  useEffect(() => {
    if (!user) return undefined
    const controller = new AbortController()
    Promise.all([
      getResumeReports(controller.signal).catch(() => ({ items: [] })),
      getMyGithubRepositories(controller.signal).catch(() => ({ items: [] })),
    ]).then(([resumePayload, githubPayload]) => {
      setResumeReports(Array.isArray(resumePayload?.items) ? resumePayload.items : [])
      setGithubProjects(Array.isArray(githubPayload?.items) ? githubPayload.items : [])
    })
    return () => controller.abort()
  }, [user])

  const selectedResume = useMemo(
    () => resumeReports.find((item) => String(item.id) === String(resumeReportId)) || null,
    [resumeReportId, resumeReports],
  )
  const selectedProject = useMemo(
    () => githubProjects.find((item) => String(item.id || item.fullName || item.full_name) === String(projectId)) || null,
    [githubProjects, projectId],
  )

  const filteredRoles = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    return ROLES.filter((item) => {
      const matchCategory = activeCategory === 'all' || item.category === activeCategory
      const haystack = [
        item.name,
        item.categoryName,
        ...item.majors,
        ...item.skills,
        ...item.questions,
        item.work,
        item.resume,
      ].join(' ').toLowerCase()
      return matchCategory && (!q || haystack.includes(q))
    })
  }, [activeCategory, keyword])

  const contextPayload = useCallback(() => ({
    roleName: role?.name,
    roleCategory: role?.categoryName,
    majors: role?.majors,
    skills: role?.skills,
    resumeReport: selectedResume,
    project: selectedProject,
    jdText,
    duration,
    locale: language === 'en' ? 'en' : 'zh',
  }), [duration, jdText, language, role, selectedProject, selectedResume])

  async function runGenerator(type) {
    if (!role) return
    setError('')
    setLimitError(null)
    setLoading(type)
    try {
      if (type === 'intro') setIntro((await generateInterviewSelfIntro(contextPayload())).result)
      if (type === 'project') setProjectTalk((await generateInterviewProjectTalk(contextPayload())).result)
      if (type === 'questions') {
        const payload = await generateInterviewQuestions(contextPayload())
        setQuestionPack(payload)
        setActiveQuestion(payload?.questions?.[0]?.question || '')
      }
      if (typeof refreshUser === 'function') refreshUser().catch(() => {})
    } catch (apiError) {
      if (isFeatureLimitExceeded(apiError)) setLimitError(apiError)
      else setError(apiError.message || '生成失败，请稍后重试')
    } finally {
      setLoading('')
    }
  }

  async function scoreAnswer() {
    if (!activeQuestion || !answer.trim()) return
    setLoading('simulate')
    setError('')
    try {
      setReview(await simulateInterviewAnswer({ ...contextPayload(), question: activeQuestion, answer }))
    } catch (apiError) {
      if (isFeatureLimitExceeded(apiError)) setLimitError(apiError)
      else setError(apiError.message || '评分失败，请稍后重试')
    } finally {
      setLoading('')
    }
  }

  async function copy(value) {
    try {
      await navigator.clipboard.writeText(String(value || ''))
    } catch {
      // Keep visible text as fallback.
    }
  }

  if (role) {
    return (
      <main className="page-container interview-page interview-detail-page">
        <Link className="secondary-button student-back-button" to="/interview"><ArrowLeft size={16} />返回面试准备</Link>
        <section className="interview-detail-hero">
          <span>{role.categoryName}</span>
          <h1>{role.name}</h1>
          <p>{role.routeSummary}</p>
          <div className="interview-hero-actions">
            <Link className="primary-button" to={assistantUrl(role)}><MessageSquareText size={17} />开始定向准备</Link>
            <button className="secondary-button" type="button" onClick={() => runGenerator('intro')}><Sparkles size={17} />生成自我介绍</button>
            <button className="secondary-button" type="button" onClick={() => runGenerator('questions')}><FileQuestion size={17} />生成岗位面试题</button>
            <Link className="secondary-button" to="/more/resume"><FileText size={17} />优化简历</Link>
            <Link className="secondary-button" to="/more/ai-tools">查看相关工具</Link>
          </div>
        </section>

        <p className="resume-mode-tip">{quotaText(user, language)}</p>
        {limitError && <FeatureLimitCard message={limitError.message} />}
        {error && <p className="student-form-error">{error}</p>}

        <section className="interview-detail-grid">
          <article className="interview-panel">
            <h2>岗位基础信息</h2>
            <dl className="interview-detail-dl">
              <dt>岗位名称</dt><dd>{role.name}</dd>
              <dt>所属方向</dt><dd>{role.categoryName}</dd>
              <dt>推荐专业</dt><dd>{role.majors.join(' / ')}</dd>
              <dt>适合人群</dt><dd>{role.fit}</dd>
            </dl>
          </article>
          <article className="interview-panel">
            <h2>必备能力</h2>
            <div className="interview-major-tags">
              {role.skills.map((skill) => <span key={skill}>{skill}</span>)}
            </div>
            <p>{role.work}</p>
            <p>{role.resume}</p>
          </article>
        </section>

        <section className="interview-panel">
          <h2>常见面试问题</h2>
          <div className="interview-question-bank">
            {role.questionDetails.map((item) => (
              <article key={item.question}>
                <h3>{item.question}</h3>
                <p><strong>面试官想看什么：</strong>{item.expects}</p>
                <p><strong>回答思路：</strong>{item.structure}</p>
                <p><strong>示例回答：</strong>{item.sample}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="interview-panel">
          <h2>准备路线</h2>
          <div className="interview-step-list">
            {role.route.map((item, index) => <div key={item}><b>{index + 1}</b><span>{item}</span></div>)}
          </div>
        </section>

        <section className="interview-context-panel">
          <h2>结合你的资料生成</h2>
          <div className="resume-extra-fields">
            <label><span>自我介绍版本</span><select value={duration} onChange={(event) => setDuration(event.target.value)}><option value="30s">30 秒版本</option><option value="60s">1 分钟版本</option><option value="120s">2 分钟版本</option></select></label>
            <label><span>选择简历记录</span><select value={resumeReportId} onChange={(event) => setResumeReportId(event.target.value)}><option value="">暂不选择</option>{resumeReports.map((item) => <option key={item.id} value={item.id}>{item.fileName || `简历记录 ${item.id}`}</option>)}</select></label>
            <label><span>选择 GitHub 项目</span><select value={projectId} onChange={(event) => setProjectId(event.target.value)}><option value="">暂不选择</option>{githubProjects.map((item) => { const id = String(item.id || item.fullName || item.full_name); return <option key={id} value={id}>{item.fullName || item.full_name || item.name}</option> })}</select></label>
          </div>
          <textarea className="resume-jd-textarea" value={jdText} onChange={(event) => setJdText(event.target.value)} placeholder="可选：粘贴岗位 JD，生成更定向的面试题。" />
          <div className="interview-hero-actions">
            <button className="secondary-button" type="button" onClick={() => runGenerator('project')}><FileText size={17} />生成项目面试表达</button>
          </div>
        </section>

        {loading && <p className="resume-mode-tip"><Loader2 className="spin-icon" size={14} /> 正在生成...</p>}

        <section className="interview-ai-grid">
          {intro && (
            <article className="interview-panel">
              <h2>自我介绍准备</h2>
              <CopyBlock title="基础模板" onCopy={copy}>{intro.template}</CopyBlock>
              <CopyBlock title="针对岗位的版本" onCopy={copy}>{intro.targeted}</CopyBlock>
              <CopyBlock title="更自然的口语版" onCopy={copy}>{intro.natural}</CopyBlock>
              <ListBlock title="避坑提醒" items={intro.pitfalls} />
            </article>
          )}
          {projectTalk && (
            <article className="interview-panel">
              <h2>项目怎么讲</h2>
              <CopyBlock title="1 分钟项目介绍" onCopy={copy}>{projectTalk.oneMinute}</CopyBlock>
              <CopyBlock title="3 分钟项目介绍" onCopy={copy}>{projectTalk.threeMinutes}</CopyBlock>
              <ListBlock title="项目亮点" items={projectTalk.highlights} />
              <ListBlock title="项目难点" items={projectTalk.difficulties} />
              <ListBlock title="面试官可能追问与推荐回答" items={asList(projectTalk.followUps).map((item) => `${item.question}：${item.answer}`)} />
            </article>
          )}
        </section>

        <section className="interview-panel">
          <h2>模拟面试</h2>
          {!questionPack && <p className="career-empty">点击“生成岗位面试题”，AI 会结合岗位、简历、GitHub 项目和 JD 生成练习题。</p>}
          {questionPack?.questions?.length > 0 && (
            <div className="interview-question-bank">
              {questionPack.questions.map((item) => (
                <article key={item.question}>
                  <span>{item.type}</span>
                  <h3>{item.question}</h3>
                  <p><strong>面试官想看：</strong>{item.expects}</p>
                  <p><strong>回答思路：</strong>{item.answerStructure}</p>
                  <p><strong>示例回答：</strong>{item.sampleAnswer}</p>
                  <p><strong>避坑：</strong>{item.pitfall}</p>
                  <button type="button" onClick={() => setActiveQuestion(item.question)}>用这题模拟</button>
                </article>
              ))}
            </div>
          )}
          <div className="interview-sim-card">
            <label><span>当前模拟问题</span><input value={activeQuestion} onChange={(event) => setActiveQuestion(event.target.value)} placeholder="选择或输入一道面试题" /></label>
            <label><span>你的回答</span><textarea value={answer} onChange={(event) => setAnswer(event.target.value)} rows={5} placeholder="逐题回答，AI 会给评分和优化建议。" /></label>
            <button className="primary-button" type="button" onClick={scoreAnswer} disabled={!activeQuestion || !answer.trim() || loading === 'simulate'}>{loading === 'simulate' ? '评分中...' : '提交回答并评分'}</button>
          </div>
          {review && (
            <article className="interview-ai-block">
              <h3>面试复盘报告</h3>
              <strong>{review.score}/100</strong>
              <p>{review.feedback}</p>
              <CopyBlock title="优化后回答" onCopy={copy}>{review.improvedAnswer}</CopyBlock>
              <p><strong>追问：</strong>{review.followUpQuestion}</p>
              <ListBlock title="下一步改进" items={review.reviewBullets} />
            </article>
          )}
        </section>
      </main>
    )
  }

  return (
    <main className="page-container interview-page">
      <section className="page-heading compact-heading interview-hero">
        <div>
          <span className="eyebrow">学生求职</span>
          <h1>准备面试，把项目讲清楚</h1>
          <p>不只包含计算机岗位，也覆盖财会、运营、教育、设计、制造、医药、公考等方向。</p>
          <div className="interview-hero-actions">
            <a className="primary-button" href="#interview-role-title"><BriefcaseBusiness size={17} />选择岗位开始准备</a>
            <Link className="secondary-button" to="/career-assistant"><MessageSquareText size={17} />进入岗位定向求职助手</Link>
            <Link className="secondary-button" to="/github-analysis"><BrainCircuit size={17} />分析我的 GitHub 项目</Link>
          </div>
        </div>
        <BrainCircuit size={30} />
      </section>

      <section className="interview-panel">
        <h2>面试准备流程</h2>
        <div className="interview-step-list interview-flow-cards">
          {['确定岗位方向', '准备自我介绍', '准备项目讲解', '练习常见问题', '模拟面试并复盘'].map((item, index) => (
            <div key={item}>
              <b>{index + 1}</b>
              <span>{item}</span>
              <button type="button" onClick={() => document.getElementById('interview-role-title')?.scrollIntoView({ behavior: 'smooth' })}>开始准备</button>
              <button type="button">查看示例</button>
              <button type="button">生成内容</button>
            </div>
          ))}
        </div>
      </section>

      <section className="interview-role-section" aria-labelledby="interview-role-title">
        <div className="entry-section-heading">
          <span className="eyebrow">岗位方向</span>
          <h2 id="interview-role-title">选择你要准备的面试方向</h2>
        </div>
        <div className="interview-filter-bar">
          <label>
            <Search size={16} />
            <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜索岗位或专业，例如：会计、教育、机械、运营" />
          </label>
        </div>
        <div className="interview-category-tabs">
          {CATEGORY_TABS.map((item) => (
            <button className={activeCategory === item.id ? 'is-active' : ''} key={item.id} type="button" onClick={() => setActiveCategory(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
        {filteredRoles.length === 0 ? (
          <p className="career-empty">暂未找到该岗位，可以试试搜索专业或方向。</p>
        ) : (
          <div className="interview-role-grid">
            {filteredRoles.map((item) => <RoleCard key={item.id} role={item} />)}
          </div>
        )}
      </section>
    </main>
  )
}

export default InterviewPrepPage
